import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { IAllowedCompanies, defaultValue } from 'app/shared/model/allowed-companies.model';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<IAllowedCompanies> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/allowed-companies';

// Actions

interface IPagedAllowedCompanyQueryParams extends IQueryParams {
  nameSearch?: string;
  status?: StatusFilter;
}

export const getEntities = createAsyncThunk(
  'allowedCompanies/fetch_entity_list',
  async ({ page, size, sort, nameSearch, status }: IPagedAllowedCompanyQueryParams) => {
    const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&search=${nameSearch}&` : ''}cacheBuster=${new Date().getTime()}`;
    return axios.get<IAllowedCompanies[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'allowedCompanies/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IAllowedCompanies>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'allowedCompanies/create_entity',
  async (entity: IAllowedCompanies, thunkAPI) => {
    const result = await axios.post<IAllowedCompanies>(apiUrl, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'allowedCompanies/update_entity',
  async (entity: IAllowedCompanies, thunkAPI) => {
    const result = await axios.put<IAllowedCompanies>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'allowedCompanies/partial_update_entity',
  async (entity: IAllowedCompanies, thunkAPI) => {
    const result = await axios.patch<IAllowedCompanies>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'allowedCompanies/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IAllowedCompanies>(requestUrl);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const AllowedCompaniesSlice = createEntitySlice({
  name: 'allowedCompanies',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = AllowedCompaniesSlice.actions;

// Reducer
export default AllowedCompaniesSlice.reducer;
